import React, { useEffect, useState } from "react";
import api from "../../../services/api";
import IssueCheckModal from "../RouteDetails/components/IssueCheckModal";
import ScannedCodesModal from "../RouteDetails/components/ScannedCodesModal";
import { Header, DateRangePicker } from "../../../components/ui";
import PageLayout from "../../../components/layout/PageLayout";
import {
  FaCashRegister,
  FaUser,
  FaMapMarkerAlt,
  FaClipboardList,
  FaMoneyBillWave,
  FaBarcode,
} from "react-icons/fa";
import styles from "./FiscalChecks.module.css";

const FiscalChecks = () => {
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [showIssueCheckModal, setShowIssueCheckModal] = useState(false);
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [codesForMark, setCodesForMark] = useState([]);
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "table"

  const getMarksInfo = (receipt) => {
    let totalExpected = 0;
    let scannedCount = 0;
    receipt.products.forEach((p) => {
      if (p.is_marked) {
        totalExpected += p.actual_quantity;
        scannedCount += p.marks ? p.marks.length : 0;
      }
    });
    return { scannedCount, totalExpected };
  };

const fetchReceipts = async (range = dateRange) => {
  if (!range?.from || !range?.to) return; // нет полного диапазона – нечего грузить

  setLoading(true);
  try {
    const response = await api.get("/routes/by-date", {
      params: { startDate: range.from, endDate: range.to }, // ⚡ тут уже 2 даты
    });

    const allReceipts = response.flatMap((route) =>
      (route.receipts || []).map((r) => ({
        ...r,
        route_id: route.id,
        route_number: route.route_number,
        driver: route.driver,
        route_districts: route.route_districts,
      }))
    );

    const filtered = allReceipts.filter((r) => {
      const { scannedCount, totalExpected } = getMarksInfo(r);
      const needsCheck = !r.is_check_issued && !r.is_check_refunded;
      const allScanned =
        scannedCount === totalExpected && totalExpected > 0;
      const allowedPayment = ![
        "ПоСчету",
        "Кредит",
        "Взаимозачет",
        "Дилер",
        "БезОплаты",
      ].includes(r.payment_type);
      return needsCheck && allScanned && allowedPayment;
    });

    setReceipts(filtered);
    //setReceipts(allReceipts); // 👉 все квитанции
  } catch (err) {
    console.error("Ошибка загрузки", err);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchReceipts(dateRange);
  }, [dateRange]);

  const handleCheckIssued = (id) => {
    setReceipts((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, is_check_issued: true, status: "Закрыта" } : r
      )
    );
  };

  const renderCardView = () => (
    <div className={styles.cardsWrapper}>
      {receipts.map((r) => {
        const { scannedCount, totalExpected } = getMarksInfo(r);
        return (
          <div
            key={r.id}
            className={`${styles.receiptCard} ${
              r.is_check_issued ? styles.issuedCard : ""
            }`}
          >
            <div className={styles.receiptHeader}>
              <span className={styles.routeTag}>Маршрут №{r.route_number}</span>
              <span className={styles.receiptNumber}>
                Квитанция #{r.receipt_number}
              </span>
            </div>

            <div className={styles.receiptBody}>
              <p>
                <FaUser color="#1976d2" /> <b>Клиент:</b> {r.client_name}
              </p>
              <p>
                <FaMapMarkerAlt color="#ef6c00" /> <b>Адрес:</b>{" "}
                {r.delivery_address}
              </p>
              <p>
                <FaClipboardList color="#6a1b9a" /> <b>Тип оплаты:</b>{" "}
                {r.payment_type}
              </p>
              <p>
                <FaMoneyBillWave color="#2e7d32" /> <b>Сумма:</b> {r.to_pay}
              </p>
              <p
                className={styles.scanInfo}
                onClick={() => {
                  const codes = r.products.flatMap((p) => p.marks || []);
                  setCodesForMark(codes);
                  setShowCodesModal(true);
                }}
              >
                <FaBarcode color="#0288d1" /> <b>Скан:</b> {scannedCount} /{" "}
                {totalExpected}
              </p>
            </div>

            <div className={styles.receiptActions}>
              <button
                className={`${styles.issueBtn} ${
                  r.is_check_issued ? styles.disabledBtn : ""
                }`}
                onClick={() => {
                  if (!r.is_check_issued) {
                    setSelectedReceiptId(r.id);
                    setShowIssueCheckModal(true);
                  }
                }}
                disabled={r.is_check_issued}
              >
                {r.is_check_issued ? "Пробит" : "Пробить чек"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTableView = () => (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Маршрут</th>
            <th>Квитанция</th>
            <th>Клиент</th>
            <th>Адрес</th>
            <th>Сумма</th>
            <th>Тип оплаты</th>
            <th>Скан</th>
            <th>Действие</th>
          </tr>
        </thead>
        <tbody>
          {receipts.map((r) => {
            const { scannedCount, totalExpected } = getMarksInfo(r);
            return (
              <tr key={r.id}>
                <td>№{r.route_number}</td>
                <td>{r.receipt_number}</td>
                <td>{r.client_name}</td>
                <td>{r.delivery_address}</td>
                <td>{r.to_pay}</td>
                <td>{r.payment_type}</td>
                <td
                  className={styles.scanCell}
                  onClick={() => {
                    const codes = r.products.flatMap((p) => p.marks || []);
                    setCodesForMark(codes);
                    setShowCodesModal(true);
                  }}
                >
                  {scannedCount} / {totalExpected}
                </td>
                <td>
                  {r.is_check_issued ? (
                    <span className={styles.issued}>Пробит</span>
                  ) : (
                    <button
                      className={styles.issueBtn}
                      onClick={() => {
                        setSelectedReceiptId(r.id);
                        setShowIssueCheckModal(true);
                      }}
                    >
                      Пробить чек
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <PageLayout
      header={
        <Header
          icon={FaCashRegister}
          title="Быстрое пробитие чеков"
          subtitle="Пробивайте фискальные чеки по маршрутам"
        />
      }
      controls={
        <>
          <DateRangePicker
            onChange={(value) => {
              // value = { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
              if (value?.from && value?.to) {
                setDateRange(value);
              }
            }}
          />
          <div className={styles.viewSwitch}>
            <button
              className={`${styles.switchBtn} ${
                viewMode === "cards" ? styles.active : ""
              }`}
              onClick={() => setViewMode("cards")}
            >
              Карточки
            </button>
            <button
              className={`${styles.switchBtn} ${
                viewMode === "table" ? styles.active : ""
              }`}
              onClick={() => setViewMode("table")}
            >
              Таблица
            </button>
          </div>
        </>
      }
    >
      {loading ? (
        <p>Загрузка...</p>
      ) : receipts.length > 0 ? (
        viewMode === "cards" ? renderCardView() : renderTableView()
      ) : (
        <p>Нет квитанций на выбранную дату</p>
      )}

      {showIssueCheckModal && (
        <div
          className="modal-wrapper"
          onClick={() => setShowIssueCheckModal(false)}
        >
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <IssueCheckModal
              receiptId={selectedReceiptId}
              onClose={() => setShowIssueCheckModal(false)}
              onCheckIssued={handleCheckIssued}
            />
          </div>
        </div>
      )}

      {showCodesModal && (
        <ScannedCodesModal
          codes={codesForMark}
          onClose={() => setShowCodesModal(false)}
        />
      )}
    </PageLayout>
  );
};

export default FiscalChecks;
